import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Check, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { TRADES } from "@/lib/data";
import { EASE, FadeUp, MaskLine } from "@/components/Reveal";
import { TRADE_ICONS } from "@/components/TradeCard";

function DetailSection({ trade, index }) {
    const Icon = TRADE_ICONS[trade.icon] || Plus;
    const flip = index % 2 === 1;

    return (
        <section
            id={`trade-${trade.code}`}
            className="scroll-mt-24 border-t border-white/10 py-20 md:py-28"
        >
            <div className="container-x">
                <div
                    className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16 ${
                        flip ? "lg:[&>*:first-child]:order-2" : ""
                    }`}
                >
                    <div className="relative">
                        <span
                            className="pointer-events-none absolute -top-10 left-0 select-none font-mono text-[7rem] font-semibold leading-none opacity-[0.06] md:text-[10rem]"
                            aria-hidden="true"
                        >
                            {trade.code}
                        </span>
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.9, ease: EASE }}
                            className="relative overflow-hidden rounded-2xl border border-white/10"
                        >
                            <img
                                src={trade.image}
                                alt={trade.title}
                                loading="lazy"
                                className="card-img h-[300px] w-full object-cover md:h-[460px]"
                            />
                            <span
                                className="absolute bottom-0 left-0 h-[3px] w-full"
                                style={{ background: trade.accent }}
                            />
                        </motion.div>
                    </div>

                    <div>
                        <FadeUp>
                            <div className="flex items-center gap-3">
                                <span
                                    className="flex h-10 w-10 items-center justify-center rounded-xl border"
                                    style={{
                                        color: trade.accent,
                                        borderColor: `${trade.accent}44`,
                                        background: `${trade.accent}12`,
                                    }}
                                >
                                    <Icon size={18} />
                                </span>
                                <span className="font-mono text-sm tracking-[0.2em]" style={{ color: trade.accent }}>
                                    NACE {trade.code}
                                </span>
                            </div>
                            <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                                {trade.title}
                            </h2>
                            <p className="mt-4 text-base leading-relaxed text-slate-300">
                                {trade.tagline}
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.1}>
                            <ul className="mt-8 space-y-3">
                                {trade.scope.map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-sm text-slate-300 md:text-base">
                                        <Check
                                            size={16}
                                            className="mt-1 shrink-0"
                                            style={{ color: trade.accent }}
                                        />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </FadeUp>

                        <FadeUp delay={0.15}>
                            <Link
                                data-testid={`service-quote-link-${trade.code}`}
                                to={`/contact?trade=${trade.code}`}
                                className="group mt-10 inline-flex items-center gap-2 font-display text-sm font-semibold text-amber-400"
                            >
                                Request this service
                                <ArrowUpRight
                                    size={16}
                                    className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                />
                            </Link>
                        </FadeUp>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function Services() {
    const { hash } = useLocation();

    useEffect(() => {
        if (hash) {
            const el = document.querySelector(hash);
            if (el) {
                const t = setTimeout(
                    () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
                    100,
                );
                return () => clearTimeout(t);
            }
        }
    }, [hash]);

    return (
        <>
            <section className="relative overflow-hidden border-b border-white/10 pb-16 pt-40 md:pb-24 md:pt-48">
                <div
                    className="pointer-events-none absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse 60% 45% at 75% 20%, rgba(245,158,11,0.1), transparent 65%)",
                    }}
                />
                <div className="tech-grid absolute inset-0" aria-hidden="true" />
                <div className="container-x relative">
                    <p className="eyebrow">Services — The registry</p>
                    <h1 className="mt-6 font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl">
                        <MaskLine>Every trade,</MaskLine>
                        <MaskLine delay={0.15}>
                            <span className="text-amber-400">on the record.</span>
                        </MaskLine>
                    </h1>
                    <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300">
                        Our four service activities, listed under their official
                        NACE codes — the same numbers you&apos;ll find on our
                        registrations, our quotes and our invoices.
                    </p>

                    <div className="mt-14 border-t border-white/10">
                        {TRADES.map((t, i) => (
                            <FadeUp key={t.code} delay={i * 0.05}>
                                <a
                                    data-testid={`registry-row-${t.code}`}
                                    href={`#trade-${t.code}`}
                                    className="group grid grid-cols-12 items-center gap-3 border-b border-white/10 py-5 transition-colors duration-300 hover:bg-white/[0.03] md:py-7"
                                >
                                    <span className="col-span-3 font-mono text-xs text-slate-500 md:col-span-2 md:text-sm">
                                        /{t.code}
                                    </span>
                                    <span className="col-span-8 font-display text-base font-semibold tracking-tight text-white transition-colors group-hover:text-amber-400 md:col-span-9 md:text-2xl">
                                        {t.title}
                                    </span>
                                    <ArrowUpRight
                                        size={18}
                                        className="col-span-1 justify-self-end text-slate-500 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-amber-400"
                                    />
                                </a>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {TRADES.map((trade, i) => (
                <DetailSection key={trade.code} trade={trade} index={i} />
            ))}

            <section className="border-t border-white/10 py-20 md:py-28">
                <div className="container-x text-center">
                    <FadeUp>
                        <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                            Need more than one trade?
                        </h2>
                        <p className="mx-auto mt-4 max-w-lg text-slate-400">
                            That&apos;s exactly why we exist — one partner for the
                            whole building.
                        </p>
                        <Link to="/contact" data-testid="services-cta-link" className="btn-primary mt-8">
                            Request a quote
                            <ArrowUpRight size={16} />
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
